import { useMutation, useQuery } from "@apollo/client";
import type { GraphQLError } from "graphql";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  GET_FA_DEFINES,
  GET_GROUP_MATCHES_FINDINGS,
  GET_GROUP_THREAT_MATCHES,
  LIST_GROUP_MATCH_DOCS,
  REMOVE_GROUP_THREAT_DOC_MUTATION,
  UPLOAD_GROUP_THREAT_DOC_MUTATION,
} from "./queries";

import type {
  GetFaDefinesVulnsQuery,
  GetGroupMatchesFindingsQuery,
  GetGroupThreatMatchesQuery,
  ListGroupMatchDocsQuery,
  RemoveGroupThreatDocMutation,
  RemoveGroupThreatDocMutationVariables,
  UploadThreatDocMutationVariables,
} from "gql/graphql";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";

interface IUseMatchesQuery {
  data: GetGroupThreatMatchesQuery | undefined;
  fetchMore: () => Promise<void>;
  loading: boolean;
  refetch: () => Promise<void>;
}

interface IUseMatchesFindingsQuery {
  data: GetGroupMatchesFindingsQuery | undefined;
  loading: boolean;
}

interface IUseMatchesDocsQuery {
  data: ListGroupMatchDocsQuery | undefined;
  loading: boolean;
  refetch: () => Promise<void>;
}

interface IUseGetCriteriaQuery {
  data: GetFaDefinesVulnsQuery | undefined;
}

interface IUseUploadMatchesDocsMutation {
  loading: boolean;
  uploadMatchesDocsMutation: (
    variables: UploadThreatDocMutationVariables,
  ) => Promise<void>;
}

interface IUseDeleteMatchesDocsMutation {
  loading: boolean;
  deleteMatchesDocsMutation: (
    variables: RemoveGroupThreatDocMutationVariables,
  ) => Promise<void>;
}

const useMatchesQuery = (groupName: string): IUseMatchesQuery => {
  const { t } = useTranslation();
  const { data, fetchMore, refetch, loading } = useQuery(
    GET_GROUP_THREAT_MATCHES,
    {
      fetchPolicy: "no-cache",
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          msgError(t("groupAlerts.errorTextsad"));
          Logger.error("Couldn't load matches data", error);
        });
      },
      variables: {
        groupName,
      },
    },
  );

  const endCursor = useMemo((): string => {
    return data?.group.threatMatches.pageInfo
      ? data.group.threatMatches.pageInfo.endCursor
      : "";
  }, [data]);

  return {
    data,
    fetchMore: async (): Promise<void> => {
      await fetchMore({
        variables: {
          after: endCursor,
        },
      });
    },
    loading,
    refetch: async (): Promise<void> => {
      await refetch();
    },
  };
};

const useMatchesFindingsQuery = (
  groupName: string,
): IUseMatchesFindingsQuery => {
  const { t } = useTranslation();
  const { data, loading } = useQuery(GET_GROUP_MATCHES_FINDINGS, {
    fetchPolicy: "no-cache",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.error("Couldn't load findings data", error);
      });
    },
    variables: {
      groupName,
    },
  });

  return {
    data,
    loading,
  };
};

const useMatchesDocsQuery = (groupName: string): IUseMatchesDocsQuery => {
  const { t } = useTranslation();
  const {
    data: docsData,
    loading: loadingDocsList,
    refetch: refetchDocs,
  } = useQuery(LIST_GROUP_MATCH_DOCS, {
    fetchPolicy: "no-cache",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.error("Couldn't load matches documents data", error);
      });
    },
    variables: {
      groupName,
    },
  });

  return {
    data: docsData,
    loading: loadingDocsList,
    refetch: async (): Promise<void> => {
      await refetchDocs();
    },
  };
};

const useGetCriteriaQuery = (): IUseGetCriteriaQuery => {
  const { t } = useTranslation();
  const { data } = useQuery(GET_FA_DEFINES, {
    fetchPolicy: "cache-first",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.error("Couldn't load criteria", error);
      });
    },
  });

  return {
    data,
  };
};

const useUploadMatchesDocsMutation = (): IUseUploadMatchesDocsMutation => {
  const { t } = useTranslation();

  const [uploadMatchesDocsMutation, { loading }] = useMutation(
    UPLOAD_GROUP_THREAT_DOC_MUTATION,
    {
      onCompleted: (): void => {
        msgSuccess(
          t("group.matches.uploadDoc.success"),
          t("group.matches.uploadDoc.successTitle"),
        );
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error: GraphQLError): void => {
          msgError(t("group.matches.uploadDoc.error"));
          Logger.error("Error uploading threat doc file:", error);
        });
      },
    },
  );

  return {
    loading,
    uploadMatchesDocsMutation: async (
      variables: UploadThreatDocMutationVariables,
    ): Promise<void> => {
      await uploadMatchesDocsMutation({ variables });
    },
  };
};

const useDeleteMatchesDocsMutation = (): IUseDeleteMatchesDocsMutation => {
  const { t } = useTranslation();

  const [deleteMatchesDocsMutation, { loading }] = useMutation(
    REMOVE_GROUP_THREAT_DOC_MUTATION,
    {
      onCompleted: (data: RemoveGroupThreatDocMutation): void => {
        const successMsg = `${t("group.matches.docsTable.deleteModal.success")} ${data.removeMatchesDocument.filename}`;
        msgSuccess(successMsg);
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error: GraphQLError): void => {
          msgError(t("group.matches.deleteDoc.error"));
          Logger.error("Error deleting threat doc file:", error);
        });
      },
    },
  );

  return {
    deleteMatchesDocsMutation: async (
      variables: RemoveGroupThreatDocMutationVariables,
    ): Promise<void> => {
      await deleteMatchesDocsMutation({ variables });
    },
    loading,
  };
};

export {
  useDeleteMatchesDocsMutation,
  useGetCriteriaQuery,
  useMatchesDocsQuery,
  useMatchesFindingsQuery,
  useMatchesQuery,
  useUploadMatchesDocsMutation,
};
